import { StepExecution } from '@/types/stepExecutions.ts';
import { ExecutionStatus } from '@/types/statuses.ts';

const secureInputReasons = new Set([
  'browser_secret_binding_malformed',
  'browser_secret_binding_unavailable',
  'browser_secret_binding_missing',
  'browser_secret_binding_domain_not_allowed',
]);

export const getBrowserFailurePresentation = (
  execution?: StepExecution,
): { message: string; nextAction: string } | null => {
  if (!execution || ![ExecutionStatus.Errors, ExecutionStatus.AskUser].includes(execution.status)) {
    return null;
  }
  const reason = execution.terminal_reason_code;
  if (!reason || !secureInputReasons.has(reason)) return null;

  const diagnostic = execution.terminal_reason_context?.browser_failure_diagnostic;
  const matchingDiagnostic =
    diagnostic?.schema_version === 'browser_failure_diagnostic.v1' &&
    diagnostic.reason_code === reason
      ? diagnostic
      : null;
  const details = matchingDiagnostic?.secure_input;
  const runtime =
    reason === 'browser_secret_binding_domain_not_allowed' || details?.phase === 'secure_input_use';
  if (!runtime) {
    return {
      message: 'Browser secure-input configuration could not be validated.',
      nextAction: 'Review the binding contract and the available secure-input keys.',
    };
  }

  let message = 'Secure-input use was blocked';
  if (
    typeof details?.observed_hostname === 'string' &&
    /^[a-zA-Z0-9.:-]{1,253}$/.test(details.observed_hostname)
  ) {
    message += ` on ${details.observed_hostname}`;
  }
  if (typeof matchingDiagnostic?.stage_label === 'string' && matchingDiagnostic.stage_label) {
    message += ` during “${matchingDiagnostic.stage_label}”`;
  }
  message += '.';
  const allowedHostnames = Array.isArray(details?.allowed_hostnames)
    ? details.allowed_hostnames
        .filter(
          (hostname) => typeof hostname === 'string' && /^[a-zA-Z0-9.-]{1,253}$/.test(hostname),
        )
        .slice(0, 10)
    : [];
  if (allowedHostnames.length) {
    message += ` The affected binding permits ${allowedHostnames.join(', ')}.`;
  }
  return {
    message,
    nextAction:
      'Verify the page or iframe recipient. If approved, explicitly update its binding; otherwise correct navigation or frame selection.',
  };
};
